import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/query';
import type {
    AxiosRequestConfig,
    AxiosRequestHeaders,
} from 'axios';
import Axios from 'axios';
import { sendApiRequest, sendNewBackendApiRequest } from '../api';

export interface AxiosBaseQueryArgs<Meta> {
  meta?: Meta;
  prepareHeaders?: (
    headers: AxiosRequestHeaders,
    api: BaseQueryApi
  ) => AxiosRequestHeaders;
  transformResponse?: (response: Response) => unknown;
}

export interface ServiceExtraOptions {
  authRequired?: boolean;
}

type NewAxiosRequestConfig = AxiosRequestConfig;

const getRequestConfig = (args: string | AxiosRequestConfig) => {
    if (typeof args === 'string') {
        return { url: args };
    }

    return args;
};

const axiosBaseQuery = <
  Args extends AxiosRequestConfig | string = AxiosRequestConfig,
  Result = unknown,
  DefinitionExtraOptions extends ServiceExtraOptions = Record<string, unknown>,
  Meta = Record<string, unknown>
>({
        prepareHeaders,
        meta,
        transformResponse,
    }: AxiosBaseQueryArgs<Meta> = {}): BaseQueryFn<
  Args,
  Result,
  unknown,
  DefinitionExtraOptions,
  Meta
> => {
    return async (args, api, extraOptions) => {
        try {
            const requestConfig: NewAxiosRequestConfig = getRequestConfig(args);

            const result = await sendApiRequest({
                ...requestConfig,
                headers: prepareHeaders
                    ? prepareHeaders(requestConfig.headers || ({} as any), api)
                    : requestConfig.headers,
                signal: api.signal,
                withCredentials: false,
                ...extraOptions,
            });

            return {
                data: transformResponse ? transformResponse(result.data) : result.data,
            };
        } catch (err: any) {

            if (!Axios.isAxiosError(err)) {
                return {
                    error: err,
                    meta,
                };
            } else {
                return {
                    error: {
                        status: err.response?.status,
                        data: err.response?.data || err.message,
                    },
                    meta,
                };
            }
        }
    };
};

/**
 * Base query for the new (ECS) backend.
 *
 * **Endpoint URLs must NOT start with `/admin`.** `REACT_APP_API_NEW_BACKEND_URL`
 * already ends in `/admin`, so a leading `/admin` produces `/admin/admin/…`
 * and a 404 whose body reads `Cannot GET /admin/admin/…`. Write
 * `/depots/:id/doors/:door/bookings`, the way `voucherApiSlice` and the twenty
 * other slices do — not `/admin/depots/…`.
 *
 * Three slices had the doubled prefix and every one of them 404'd in
 * production: locker history, group occupancy (reported as a broken
 * utilization view and misdiagnosed as a stale ECS deploy) and the whole
 * inventory surface including station creation.
 */
export const newBackendAxiosBaseQuery = <
  Args extends AxiosRequestConfig | string = AxiosRequestConfig,
  Result = unknown,
  DefinitionExtraOptions extends ServiceExtraOptions = Record<string, unknown>,
  Meta = Record<string, unknown>
>({
        prepareHeaders,
        meta,
        transformResponse,
    }: AxiosBaseQueryArgs<Meta> = {}): BaseQueryFn<
  Args,
  Result,
  unknown,
  DefinitionExtraOptions,
  Meta
> => {
    return async (args, api, extraOptions) => {
        try {
            const requestConfig: NewAxiosRequestConfig = getRequestConfig(args);

            const result = await sendNewBackendApiRequest({
                ...requestConfig,
                headers: prepareHeaders
                    ? prepareHeaders(requestConfig.headers || ({} as any), api)
                    : requestConfig.headers,
                signal: api.signal,
                withCredentials: false,
                ...extraOptions,
            });

            return {
                data: transformResponse ? transformResponse(result.data) : result.data,
            };
        } catch (err: any) {

            if (!Axios.isAxiosError(err)) {
                return {
                    error: err,
                    meta,
                };
            } else {
                return {
                    error: {
                        status: err.response?.status,
                        data: err.response?.data || err.message,
                    },
                    meta,
                };
            }
        }
    };
};

export default axiosBaseQuery;
